import * as React from 'react';
import classNames from 'classnames';
import useMergedState from 'rc-util/lib/hooks/useMergedState';

import DisabledContext from '../config-provider/DisabledContext';
import { useTabButtonStyle } from './style/tab-button';

export type TabButtonValue = string | number;

export interface TabButtonOption {
  label: React.ReactNode;
  value: TabButtonValue;
  disabled?: boolean;
}

export interface TabButtonProps {
  options?: TabButtonOption[];
  value?: TabButtonValue;
  defaultValue?: TabButtonValue;
  title?: React.ReactNode;
  disabled?: boolean;
  onChange?: (value: TabButtonValue) => void;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

export interface TabButtonRef {
  nativeElement: HTMLDivElement;
  focus: VoidFunction;
}

const EMPTY_OPTIONS: TabButtonOption[] = [];

const TabButton = React.forwardRef<TabButtonRef, TabButtonProps>((props, ref) => {
  const {
    options = EMPTY_OPTIONS,
    value,
    defaultValue,
    title,
    disabled: customDisabled,
    onChange,
    id,
    className,
    style,
  } = props;

  const contextDisabled = React.useContext(DisabledContext);
  const disabled = customDisabled ?? contextDisabled;

  const [innerValue, setInnerValue] = useMergedState(defaultValue ?? options[0]?.value, {
    value,
  });

  const divRef = React.useRef<HTMLDivElement>(null);
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = React.useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });

  const prefixCls = 'youlify-tab-button';
  const [wrapSSR, hashId] = useTabButtonStyle(prefixCls);

  const selectedIndex = options.findIndex((opt) => opt.value === innerValue);

  const focusOption = (index: number) => {
    const optionEls = tabsRef.current?.querySelectorAll<HTMLElement>(`.${prefixCls}-option`);
    optionEls?.[index]?.focus();
  };

  React.useImperativeHandle(ref, () => ({
    get nativeElement() {
      return divRef.current!;
    },
    focus: () => focusOption(selectedIndex < 0 ? 0 : selectedIndex),
  }));

  const setIndicatorIfChanged = (next: { left: number; width: number }) => {
    // Position the sliding indicator from measured DOM geometry; guard the
    // update so an unchanged measurement never re-triggers a render.
    // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
    setIndicatorStyle((prev) =>
      prev.left === next.left && prev.width === next.width ? prev : next,
    );
  };

  React.useEffect(() => {
    const tabsEl = tabsRef.current;
    /* istanbul ignore if -- the ref is always assigned once the effect runs */
    if (!tabsEl) {
      return;
    }
    if (selectedIndex < 0) {
      setIndicatorIfChanged({ left: 0, width: 0 });
      return;
    }
    const optionEls = tabsEl.querySelectorAll<HTMLElement>(`.${prefixCls}-option`);
    const el = optionEls[selectedIndex];
    /* istanbul ignore else -- a valid selectedIndex always maps to a rendered option */
    if (el) {
      setIndicatorIfChanged({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [selectedIndex, options]);

  const triggerChange = (next: TabButtonValue) => {
    if (next === innerValue) return;
    setInnerValue(next);
    onChange?.(next);
  };

  const handleSelect = (opt: TabButtonOption) => {
    if (disabled || opt.disabled) return;
    triggerChange(opt.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || options.length === 0) return;
    const enabledIndexes = options
      .map((opt, idx) => (opt.disabled ? -1 : idx))
      .filter((idx) => idx >= 0);
    if (enabledIndexes.length === 0) return;
    const current = enabledIndexes.indexOf(selectedIndex);

    let targetIndex = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      const nextPos = current < 0 ? 0 : (current + 1) % enabledIndexes.length;
      targetIndex = enabledIndexes[nextPos];
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      const prevPos =
        current < 0
          ? enabledIndexes.length - 1
          : (current - 1 + enabledIndexes.length) % enabledIndexes.length;
      targetIndex = enabledIndexes[prevPos];
    }

    if (targetIndex >= 0) {
      e.preventDefault();
      triggerChange(options[targetIndex].value);
      focusOption(targetIndex);
    }
  };

  const containerCls = classNames(prefixCls, hashId, className, {
    [`${prefixCls}-disabled`]: disabled,
  });

  return wrapSSR(
    <div ref={divRef} id={id} className={containerCls} style={style}>
      {title != null && title !== '' && (
        <span className={classNames(`${prefixCls}-title`, hashId)}>{title}</span>
      )}
      <div
        ref={tabsRef}
        className={classNames(`${prefixCls}-tabs`, hashId)}
        role="tablist"
        aria-disabled={disabled}
      >
        <div
          className={classNames(`${prefixCls}-indicator`, hashId)}
          style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
        />
        {options.map((opt) => {
          const isSelected = opt.value === innerValue;
          const isDisabled = disabled || !!opt.disabled;
          const optionCls = classNames(`${prefixCls}-option`, hashId, {
            [`${prefixCls}-option-selected`]: isSelected,
            [`${prefixCls}-option-disabled`]: isDisabled,
          });
          return (
            <div
              key={opt.value}
              className={optionCls}
              role="tab"
              aria-selected={isSelected}
              aria-disabled={isDisabled}
              tabIndex={isDisabled || !isSelected ? -1 : 0}
              onClick={() => handleSelect(opt)}
              onKeyDown={handleKeyDown}
            >
              {opt.label}
            </div>
          );
        })}
      </div>
    </div>,
  );
});

TabButton.displayName = 'TabButton';

export default TabButton;
